/**
 * TrialBanner Component
 *
 * Displays a banner for trial users showing trial status.
 * - Shows warning when trial is ending soon (7 days or less)
 * - Shows urgent warning when trial is ending very soon (3 days or less)
 * - Shows expired message when trial has ended (during grace period)
 */

import React from "react";
import { Alert, Button, Group, Text } from "@mantine/core";
import { IconClock, IconAlertCircle } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useFeatureAccess } from "../../context/FeatureAccessContext";

export const TrialBanner: React.FC = () => {
  const navigate = useNavigate();
  const { access, isTrialExpired, daysUntilTrialExpires } = useFeatureAccess();

  // Don't show if not loaded or not on trial
  if (!access || access.subscription_tier !== "trial") {
    return null;
  }

  const daysLeft = daysUntilTrialExpires();

  // Trial expired (in grace period - backend hasn't blocked yet)
  if (isTrialExpired()) {
    return (
      <Alert
        color="red"
        icon={<IconAlertCircle size={20} />}
        mb="md"
        variant="filled"
      >
        <Group justify="space-between" wrap="nowrap" gap="md">
          <Text fw={500} size="sm">
            Your free trial has expired. Subscribe now to continue using Pagoda.
          </Text>
          <Button
            color="white"
            variant="white"
            size="xs"
            onClick={() => navigate("/admin/settings/subscription")}
          >
            Subscribe Now
          </Button>
        </Group>
      </Alert>
    );
  }

  // Trial ending very soon (3 days or less) - urgent
  if (daysLeft !== null && daysLeft <= 3) {
    return (
      <Alert
        color="red"
        icon={<IconAlertCircle size={20} />}
        mb="md"
        variant="light"
      >
        <Group justify="space-between" wrap="nowrap" gap="md">
          <Text size="sm">
            <Text component="span" fw={600}>
              {daysLeft === 0
                ? "Last day!"
                : `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`}
            </Text>{" "}
            Your trial is ending soon.
          </Text>
          <Button
            color="red"
            variant="filled"
            size="xs"
            onClick={() => navigate("/admin/settings/subscription")}
          >
            Subscribe Now
          </Button>
        </Group>
      </Alert>
    );
  }

  // Trial ending soon (7 days or less) - warning
  if (daysLeft !== null && daysLeft <= 7) {
    return (
      <Alert
        color="orange"
        icon={<IconClock size={20} />}
        mb="md"
        variant="light"
      >
        <Group justify="space-between" wrap="nowrap" gap="md">
          <Text size="sm">
            Your trial expires in {daysLeft} day{daysLeft !== 1 ? "s" : ""}.
          </Text>
          <Button
            variant="light"
            color="orange"
            size="xs"
            onClick={() => navigate("/admin/settings/subscription")}
          >
            View Plans
          </Button>
        </Group>
      </Alert>
    );
  }

  // More than 7 days left - no banner
  return null;
};

export default TrialBanner;
